import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Text } from '@/components/Text';
import api from '@/api/axios';
import { isPatientApp, isTherapistApp } from '@/config/constants';
import SignUpMessages from './SignUpMessages';
import { styles } from './styles';
import { getInstitutions, Institution } from '@/api/services/institutions';
import { TherapistSignUpForm } from './TherapistSignUpForm';
import { PatientSignUpForm } from './PatientSignUpForm';
import { SignUpFormData } from './SignUpFormData';

export function SignUpController() {
  const {
    control,
    formState: { errors, isSubmitted },
    handleSubmit,
    setValue,
    watch,
  } = useForm<SignUpFormData>({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const email = useRef<string | null>(null);
  const filledPassword = watch('password', '');

  const [isUseOfDataConfirmed, setUseOfDataConfirmation] = useState(false);
  const [isDataStudyConsent, setDataStudyConsent] = useState(false);
  const [isNewsletterConfirmed, setNewsletterConfirmation] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isModalErrorVisible, setModalErrorVisible] = useState(false);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [setNewsletter] = useState(false);

  const showNewsletter = isPatientApp ? setNewsletter : !setNewsletter;

  const errorMessageCondition =
    isSubmitted && (Object.keys(errors).length !== 0 || !isUseOfDataConfirmed);

  const onSignUpPressed = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    institution?: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (Object.keys(errors).length === 0) {
      if (isUseOfDataConfirmed) {
        const credentials: SignUpFormData = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email.trim(),
          password: data.password,
          dataProcessingAgreement: isUseOfDataConfirmed,
          studyConsent: isDataStudyConsent,
          roles: [],
          digaUser: undefined,
          registrationChannel: '',
          newsletter: undefined,
          institution: undefined,
        };

        if (isPatientApp) {
          credentials.roles = ['client'];
          credentials.digaUser = true;
          credentials.registrationChannel = 'neolexon-aphasie';
        } else {
          credentials.roles = ['user'];
          credentials.newsletter = isNewsletterConfirmed;
          credentials.registrationChannel = 'neolexon-therapeut';
          credentials.institution = data.institution;
        }

        api
          .post('/api/auth/signup', credentials, {
            headers: {
              'Content-Type': 'application/json',
            },
          })
          .then((response) => {
            email.current = credentials.email;
            setValue('email', data.email);
            setModalVisible(true);
            return response;
          })
          .catch(() => {
            setModalErrorVisible(true);
          });
      }
    }
  };

  useEffect(() => {
    const initializeTherapistInstitution = async () => {
      try {
        const institutions = await getInstitutions();
        setInstitutions(institutions);
      } catch {
        setInstitutions([]);
      }
    };

    if (isTherapistApp) {
      initializeTherapistInstitution();
    }
  }, []);

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView>
        {isTherapistApp ? (
          <TherapistSignUpForm
            control={control}
            errors={errors}
            institutions={institutions}
            handleSubmit={handleSubmit(onSignUpPressed)}
            isUseOfDataConfirmed={isUseOfDataConfirmed}
            setUseOfDataConfirmation={setUseOfDataConfirmation}
            isDataStudyConsent={isDataStudyConsent}
            setDataStudyConsent={setDataStudyConsent}
            isNewsletterConfirmed={isNewsletterConfirmed}
            setNewsletterConfirmation={setNewsletterConfirmation}
            showNewsletter={showNewsletter}
            isSubmitted={isSubmitted}
            filledPassword={filledPassword}
          />
        ) : (
          <PatientSignUpForm
            control={control}
            errors={errors}
            handleSubmit={handleSubmit(onSignUpPressed)}
            isUseOfDataConfirmed={isUseOfDataConfirmed}
            setUseOfDataConfirmation={setUseOfDataConfirmation}
            isDataStudyConsent={isDataStudyConsent}
            setDataStudyConsent={setDataStudyConsent}
            isNewsletterConfirmed={isNewsletterConfirmed}
            setNewsletterConfirmation={setNewsletterConfirmation}
            showNewsletter={showNewsletter}
            isSubmitted={isSubmitted}
            filledPassword={filledPassword}
          />
        )}

        {!!errorMessageCondition && (
          <Text
            testID="registration-global-error"
            style={styles.registrationErrorMessage}
          >
            Bitte überprüfen Sie Ihre Eingaben und versuchen es erneut.
          </Text>
        )}

        <SignUpMessages
          isModalVisible={isModalVisible}
          setModalVisible={setModalVisible}
          isModalErrorVisible={isModalErrorVisible}
          setModalErrorVisible={setModalErrorVisible}
          email={email.current}
        />
      </ScrollView>
    </SafeAreaView>
  );
}
